import {
  isAdminRole,
  TeamSpace,
  type UpdateContributorRole,
  type UpdateTeamSpaceRoles,
} from "@prismicio/editor-fields"
import { pluralize } from "@prismicio/editor-support/String"
import {
  ActionList,
  ActionListItem,
  AnimatedElement,
  Avatar,
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  IconButton,
  type IconName,
  RadioButton,
  RadioGroup,
  ScrollArea,
  SearchInput,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
} from "@prismicio/editor-ui"
import {
  type Dispatch,
  type PropsWithChildren,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react"

import { Flag } from "../../../app/components/locales/Flag"
import { useRepositoryV2 } from "../../../app/services/repository"
import { useRoles } from "../../../app/services/role"
import { useRepositoryLocalesQuery } from "../../../settings/locales/useRepositoryLocales"
import { getAuthorDisplayName } from "../../DocumentListBody/ActionBar/utils"
import {
  getEffectiveRole,
  getTeamSpaceRoles,
  hasRolesPerLocale,
  type TeamSpaceRoles,
  updateRolesPerLocale,
} from "./CustomRoles.utils"

const everyoneInRepositoryImage =
  "https://images.prismic.io/page-builder-assets/Z7Q6gJ7c43Q3f5Tf_everyone.png?auto=format,compress"

interface CustomRolesProps {
  teamSpace?: TeamSpace
  roleUpdates: UpdateTeamSpaceRoles
  setRoleUpdates: Dispatch<SetStateAction<UpdateTeamSpaceRoles>>
}

interface UpdateRoleArgs {
  localeId: string
  role: string
}

export function CustomRoles(props: CustomRolesProps) {
  const { teamSpace, roleUpdates, setRoleUpdates } = props

  const [search, setSearch] = useState("")
  const isSearchFilled = search.length > 0
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState("everyone")

  const { repository } = useRepositoryV2()

  const roles = useRoles(
    teamSpace
      ? {
          teamSpaceIds: [teamSpace.id],
        }
      : {
          scopes: ["repository"],
        },
  )

  const teamSpaceRoles = getTeamSpaceRoles({
    roles,
    teamSpaceId: teamSpace?.id,
  })

  const members = repository?.users ?? []
  const contributors = members.filter(
    (author) =>
      !isAdminRole(roles.repository?.rolesPerUser[author.id] ?? "Readonly"),
  )

  const filteredContributors = contributors.filter(
    (author) =>
      getAuthorDisplayName(author)
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()) ||
      author.email.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  )

  const updateRole = (args: UpdateRoleArgs) => {
    const { localeId, role } = args
    setRoleUpdates((roleUpdates) =>
      updateRolesPerLocale({
        roleUpdates,
        userId: selectedUserId,
        update: {
          action: "update",
          localeId,
          role: role as UpdateContributorRole,
        },
      }),
    )
  }

  const contributorsWithRoles = contributors.filter((user) =>
    hasRolesPerLocale({
      roles: teamSpaceRoles,
      roleUpdates,
      userId: user.id,
    }),
  )

  const searchInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    // Makes sure that the search input does not get focus restored
    // for a split second after refocusing the window
    const handleWindowFocus = () => {
      if (document.activeElement === searchInputRef.current) {
        searchInputRef.current?.blur()
      }
    }

    window.addEventListener("focus", handleWindowFocus)
    return () => window.removeEventListener("focus", handleWindowFocus)
  }, [])

  const hasContributors = contributors.length > 0

  const showNoContributorsInfo =
    (isSearchFocused || isSearchFilled) && !hasContributors

  const showNoUsersFoundInfo =
    isSearchFilled && hasContributors && filteredContributors.length === 0

  const showUsersList = !showNoContributorsInfo && !showNoUsersFoundInfo

  return (
    <Box display="grid" gridTemplateColumns="1fr 2fr" height="100%" gap={16}>
      <Box
        display="flex"
        flexDirection="column"
        gap={8}
        overflow="hidden"
        padding={{ top: 16 }}
      >
        <Box
          backgroundColor="grey3"
          borderRadius={6}
          height={32}
          flexShrink={0}
        >
          <SearchInput
            ref={searchInputRef}
            placeholder="Search by name or email"
            value={search}
            onValueChange={setSearch}
            inlineAdornmentPadding={8}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />
        </Box>

        {showNoUsersFoundInfo && (
          <InfoCard
            icon="alert"
            title="Search and add people"
            description={`No users found. Try a different\nname or email address.`}
            paddingBottom={56}
          />
        )}

        {showNoContributorsInfo && (
          <InfoCard
            icon="alert"
            title="No contributors found"
            description="This repository doesn't have any contributors yet. You can assign custom roles to users who aren't administrators."
            paddingBottom={56}
          />
        )}

        {showUsersList && (
          <ScrollArea
            stableScrollbar={false}
            style={{
              padding: 1,
              paddingBottom: 17,
            }}
          >
            <ActionList variant="compact">
              {isSearchFilled || isSearchFocused ? (
                filteredContributors.map((author) => (
                  <ActionListItem
                    key={author.id}
                    description={author.email}
                    textVariant="bold"
                    selectedVariant="shadow"
                    renderStartIcon={() => (
                      <Avatar
                        imageUrl={author.uploadedAvatar}
                        firstName={author.firstname}
                        lastName={author.lastname}
                        size={40}
                      />
                    )}
                    /*
                     * Prevent blurring the search input when clicking on an item.
                     * The order of events is: onMouseDown, onBlur, onFocus, onClick
                     * By calling e.preventDefault() in onMouseDown on soon-to-be-focused element,
                     * we prevent the onBlur event from being called on the previously focused element
                     * and allow onClick to happen before unmounting ActionListItems.
                     * The search input gets blurred manually in the onClick handler.
                     */
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (
                        !hasRolesPerLocale({
                          roles: teamSpaceRoles,
                          roleUpdates,
                          userId: author.id,
                        })
                      ) {
                        setRoleUpdates((updateRoles) =>
                          updateRolesPerLocale({
                            roleUpdates: updateRoles,
                            userId: author.id,
                            update: {
                              action: "create",
                            },
                          }),
                        )
                      }
                      setSearch("")
                      setSelectedUserId(author.id)
                      searchInputRef.current?.blur()
                    }}
                    noWrap
                  >
                    {getAuthorDisplayName(author)}
                  </ActionListItem>
                ))
              ) : (
                <>
                  <ActionListItem
                    description={`${contributors.length} ${pluralize(contributors.length, "contributor")}`}
                    textVariant="bold"
                    selectedVariant="shadow"
                    renderStartIcon={() => (
                      <Avatar imageUrl={everyoneInRepositoryImage} size={40} />
                    )}
                    onClick={() => setSelectedUserId("everyone")}
                    selected={selectedUserId === "everyone"}
                    noWrap
                  >
                    Everyone in the repository
                  </ActionListItem>
                  {contributorsWithRoles.map((author) => (
                    <ActionListItem
                      key={author.id}
                      description={author.email}
                      textVariant="bold"
                      selectedVariant="shadow"
                      renderStartIcon={() => (
                        <Avatar
                          imageUrl={author.uploadedAvatar}
                          firstName={author.firstname}
                          lastName={author.lastname}
                          size={40}
                        />
                      )}
                      onClick={() => setSelectedUserId(author.id)}
                      selected={selectedUserId === author.id}
                      endOverlay={
                        <IconButton
                          icon="close"
                          color="tomato"
                          onClick={() => {
                            setRoleUpdates((roleUpdates) =>
                              updateRolesPerLocale({
                                roleUpdates,
                                userId: author.id,
                                update: {
                                  action: "delete",
                                },
                              }),
                            )
                            setSelectedUserId("everyone")
                          }}
                        />
                      }
                      noWrap
                    >
                      {getAuthorDisplayName(author)}
                    </ActionListItem>
                  ))}
                </>
              )}
            </ActionList>
          </ScrollArea>
        )}
      </Box>
      <Box display="flex" flexDirection="column" gap={8} overflow="hidden">
        {isSearchFocused || isSearchFilled ? (
          <InfoCard
            icon="alert"
            title="Please select a user"
            description={`Add roles per user by typing\ntheir name or email address`}
          />
        ) : (
          <ScrollArea style={{ paddingBlock: 16 }}>
            <RolesPerLocaleTable
              userId={selectedUserId}
              roleUpdates={roleUpdates}
              updateRole={updateRole}
              teamSpaceRoles={teamSpaceRoles}
            />
          </ScrollArea>
        )}
      </Box>
    </Box>
  )
}

export function CustomRolesSkeleton() {
  return (
    <Box display="grid" gridTemplateColumns="1fr 2fr" height="100%" gap={16}>
      <Box
        display="flex"
        flexDirection="column"
        gap={8}
        overflow="hidden"
        padding={{ top: 16 }}
      >
        <Skeleton height={32} />
        <ScrollArea
          stableScrollbar={false}
          style={{
            display: "flex",
            flexDirection: "column",
            padding: 1,
            paddingBottom: 17,
            gap: 8,
          }}
        >
          <Skeleton height={58} />
        </ScrollArea>
      </Box>
      <Box display="flex" flexDirection="column" gap={8} overflow="hidden">
        <ScrollArea style={{ paddingBlock: 16 }}>
          <RolesPerLocaleTableSkeleton />
        </ScrollArea>
      </Box>
    </Box>
  )
}

interface RolesPerLocaleTableProps {
  userId: string
  roleUpdates: UpdateTeamSpaceRoles
  updateRole: (args: UpdateRoleArgs) => void
  teamSpaceRoles: TeamSpaceRoles
}

function RolesPerLocaleTable(props: RolesPerLocaleTableProps) {
  const { userId, roleUpdates, updateRole, teamSpaceRoles } = props
  const { data: repositoryLocales } = useRepositoryLocalesQuery()

  return (
    <AnimatedElement
      animation={{ name: "slide", offset: -10 }}
      enterDuration={150}
    >
      <RolesPerLocaleTableLayout key={userId}>
        {repositoryLocales.map((locale) => (
          <LocaleRow
            key={locale.id}
            name={locale.label}
            localeId={locale.id}
            userId={userId}
            roleUpdates={roleUpdates}
            updateRole={updateRole}
            teamSpaceRoles={teamSpaceRoles}
          />
        ))}
      </RolesPerLocaleTableLayout>
    </AnimatedElement>
  )
}

function RolesPerLocaleTableSkeleton() {
  return (
    <RolesPerLocaleTableLayout>
      {Array.from({ length: 5 }).map((_, index) => (
        <TableRow key={index}>
          {Array.from({ length: 4 }).map((_, index) => (
            <TableCell key={index}>
              <Skeleton height={24} width="100%" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </RolesPerLocaleTableLayout>
  )
}

function RolesPerLocaleTableLayout(props: PropsWithChildren) {
  const { children } = props

  return (
    <Table columnLayout="3fr 1fr 1fr 1fr" isInteractive={false}>
      <TableHead>
        <TableRow>
          <HeaderCell>Locale</HeaderCell>
          <HeaderCell align="center">Read-only</HeaderCell>
          <HeaderCell align="center">Writer</HeaderCell>
          <HeaderCell align="center">Publisher</HeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  )
}

interface LocaleRowProps {
  name: string
  teamSpaceRoles: TeamSpaceRoles
  roleUpdates: UpdateTeamSpaceRoles
  updateRole: (args: UpdateRoleArgs) => void
  userId: string
  localeId: string
}

function LocaleRow(props: LocaleRowProps) {
  const { name, localeId, updateRole, roleUpdates, userId, teamSpaceRoles } =
    props

  function onChange(value: string) {
    updateRole({
      localeId,
      role: value,
    })
  }

  return (
    <RadioGroup
      value={getEffectiveRole({
        roles: teamSpaceRoles,
        roleUpdates,
        userId,
        localeId,
      })}
      onChange={onChange}
      asChild
    >
      <TableRow>
        <TableCell>
          <Box
            display="flex"
            flexDirection="row"
            overflow="hidden"
            alignItems="center"
          >
            <Flag locale={localeId} />
            <Text variant="bold" noWrap sx={{ marginLeft: 8 }}>
              {name}
            </Text>
            <Text
              variant="normal"
              color="grey11"
              noWrap
              sx={{ marginLeft: 4, flexShrink: 0 }}
            >
              ({localeId})
            </Text>
          </Box>
        </TableCell>
        <TableCell align="center">
          <RadioButton value="Readonly" />
        </TableCell>
        <TableCell align="center">
          <RadioButton value="Writer" />
        </TableCell>
        <TableCell align="center">
          <RadioButton value="Manager" />
        </TableCell>
      </TableRow>
    </RadioGroup>
  )
}

interface HeaderCellProps {
  children: ReactNode
  align?: "center"
}

function HeaderCell(props: HeaderCellProps) {
  const { children, align } = props
  return (
    <TableCell align={align}>
      <Text color="grey11" noSelect noWrap variant="smallBold">
        {children}
      </Text>
    </TableCell>
  )
}

interface InfoCardProps {
  title: string
  description: string
  icon: IconName
  paddingBottom?: 56
}

function InfoCard(props: InfoCardProps) {
  const { title, description, icon, paddingBottom } = props

  return (
    <ScrollArea
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        minHeight: "100%",
        flexShrink: 0,
        paddingBottom,
      }}
    >
      <BlankSlate inlinePadding={false} gap={0}>
        <BlankSlateIcon name={icon} />
        <BlankSlateTitle size="h4" sx={{ marginTop: 8 }}>
          {title}
        </BlankSlateTitle>
        <BlankSlateDescription>{description}</BlankSlateDescription>
      </BlankSlate>
    </ScrollArea>
  )
}
